import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyExchangeLineItemKey,
  ShopifyExchangeLineItemToReturnKey,
  ShopifyOrderKey,
  ShopifyRefundKey,
  ShopifyRefundToReturnKey,
  ShopifyReturnKey,
  ShopifyReturnLineItemKey,
  ShopifyReturnLineItemToReturnKey,
  ShopifyReturnShippingFeeKey,
  ShopifyReturnShippingFeeToReturnKey,
  ShopifyReturnToExchangeLineItemsKey,
  ShopifyReturnToOrderKey,
  ShopifyReturnToRefundsKey,
  ShopifyReturnToReturnLineItemsKey,
  ShopifyReturnToReturnShippingFeesKey,
  ShopifyReturnToReverseFulfillmentOrdersKey,
  ShopifyReturnToShopKey,
  ShopifyReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderToReturnKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// Hardcoded field keys to preserve deterministic keys when shopifyFieldKey was added
const orderFieldEpochKey = "FieldStorageEpoch-DataModel-Shopify-Return-order-initial";
const totalQuantityFieldKey = "ModelField-DataModel-Shopify-Return-totalQuantity";
const totalQuantityStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-Return-totalQuantity-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyReturnKey,
  "2024-07": {
    name: "Shopify Return",
    graphQLTypename: "Return",
    fields: [
      {
        name: "Decline",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "decline",
            graphQLSelection: `
            note
            reason
          `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Exchange Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyReturnToExchangeLineItemsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "exchange_line_items",
            graphQLFieldKey: "exchangeLineItems",
          },
        },
        configuration: {
          relatedModelKey: ShopifyExchangeLineItemKey,
          inverseFieldKey: ShopifyExchangeLineItemToReturnKey,
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
          },
        },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReturnToOrderKey,
        initialStorageEpochKey: orderFieldEpochKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "order_id",
            graphQLFieldKey: "order",
            graphQLSelection: "id",
          },
        },
      },
      {
        name: "Return Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyReturnToReturnLineItemsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "return_line_items",
            graphQLFieldKey: "returnLineItems",
          },
        },
        configuration: {
          relatedModelKey: ShopifyReturnLineItemKey,
          inverseFieldKey: ShopifyReturnLineItemToReturnKey,
        },
      },
      {
        name: "Reverse Fulfillment Orders",
        fieldType: FieldType.HasMany,
        key: ShopifyReturnToReverseFulfillmentOrdersKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "reverseFulfillmentOrders",
          },
        },
        configuration: {
          relatedModelKey: ShopifyReverseFulfillmentOrderKey,
          inverseFieldKey: ShopifyReverseFulfillmentOrderToReturnKey,
        },
      },
      {
        name: "Return Shipping Fees",
        fieldType: FieldType.HasMany,
        key: ShopifyReturnToReturnShippingFeesKey,
        configuration: {
          relatedModelKey: ShopifyReturnShippingFeeKey,
          inverseFieldKey: ShopifyReturnShippingFeeToReturnKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "return_shipping_fees",
            graphQLFieldKey: "returnShippingFees",
            graphQLSelection: `
          id
          amountSet {
            presentmentMoney {
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }
          }
          `,
            selectForHasMany: true,
          },
        },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "status",
            graphQLFieldKey: "status",
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [{ name: "CANCELED" }, { name: "CLOSED" }, { name: "DECLINED" }, { name: "OPEN" }, { name: "REQUESTED" }],
        },
      },
      {
        name: "Total Quantity",
        fieldType: FieldType.Number,
        key: totalQuantityFieldKey,
        initialStorageEpochKey: totalQuantityStorageEpochKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "total_return_line_items",
            graphQLFieldKey: "totalQuantity",
          },
        },
      },

      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReturnToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order",
      resource: "order.returns",
    },
  },
  "2025-04": {
    fields: [
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
            availableInWebhook: ["returns/approve", "returns/request"],
          },
        },
      },
      {
        name: "Decline",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "decline",
            graphQLFieldKey: "decline",
            graphQLSelection: `
            note
            reason
          `,
            graphQLOnlyField: true,
            availableInWebhook: ["returns/decline"],
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
            availableInWebhook: [
              "returns/approve",
              "returns/cancel",
              "returns/close",
              "returns/decline",
              "returns/reopen",
              "returns/request",
            ],
          },
        },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "status",
            graphQLFieldKey: "status",
            isShopifyEnum: true,
            availableInWebhook: [
              "returns/approve",
              "returns/cancel",
              "returns/close",
              "returns/decline",
              "returns/reopen",
              "returns/request",
            ],
          },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [{ name: "CANCELED" }, { name: "CLOSED" }, { name: "DECLINED" }, { name: "OPEN" }, { name: "REQUESTED" }],
        },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReturnToOrderKey,
        initialStorageEpochKey: orderFieldEpochKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "order_id",
            availableInWebhook: ["returns/cancel", "returns/close", "returns/reopen"],
          },
        },
      },
      {
        name: "Return Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyReturnToReturnLineItemsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "return_line_items",
            graphQLFieldKey: "returnLineItems",
            availableInWebhook: ["returns/approve", "returns/request", "returns/update"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyReturnLineItemKey,
          inverseFieldKey: ShopifyReturnLineItemToReturnKey,
        },
      },
      {
        name: "Exchange Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyReturnToExchangeLineItemsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "exchange_line_items",
            graphQLFieldKey: "exchangeLineItems",
            availableInWebhook: ["returns/approve", "returns/request"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyExchangeLineItemKey,
          inverseFieldKey: ShopifyExchangeLineItemToReturnKey,
        },
      },
      {
        name: "Return Shipping Fees",
        fieldType: FieldType.HasMany,
        key: ShopifyReturnToReturnShippingFeesKey,
        configuration: {
          relatedModelKey: ShopifyReturnShippingFeeKey,
          inverseFieldKey: ShopifyReturnShippingFeeToReturnKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "return_shipping_fees",
            graphQLFieldKey: "returnShippingFees",
            graphQLSelection: `
          id
          amountSet {
            presentmentMoney {
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }
          }
          `,
            selectForHasMany: true,
            availableInWebhook: ["returns/approve", "returns/request", "returns/update"],
          },
        },
      },
      {
        name: "Total Quantity",
        fieldType: FieldType.Number,
        key: totalQuantityFieldKey,
        initialStorageEpochKey: totalQuantityStorageEpochKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "total_return_line_items",
            graphQLFieldKey: "totalQuantity",
            availableInWebhook: ["returns/approve", "returns/request"],
          },
        },
      },
      {
        name: "Total Exchange Line Items",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "total_exchange_line_items",
            availableInWebhook: ["returns/approve", "returns/request"],
          },
        },
      },
      {
        name: "Refunds",
        fieldType: FieldType.HasMany,
        key: ShopifyReturnToRefundsKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "refunds" } },
        configuration: {
          relatedModelKey: ShopifyRefundKey,
          inverseFieldKey: ShopifyRefundToReturnKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "orders",
      resource: "order.returns",
    },
  },
};

export const ShopifyReturn = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
